'use es6';

import { isEmpty } from 'underscore';
import { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FATAL_ERROR_CODES, FATAL_ERROR_TYPES, APP_ERRORS } from 'ContentEditorUI/lib/app/constants';
import ErrorPage from 'ContentEditorUI/components/app/ErrorPage';
import UnauthorizedPage from 'ContentEditorUI/components/app/UnauthorizedPage';
import UnknownAppError from 'ContentEditorUI/components/app/UnknownAppError';
import SecurityViolationPage from 'ContentEditorUI/components/app/SecurityViolationPage';
import { getFatalError } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import { getCanContactSupport } from 'ContentEditorUI/redux/selectors/authSelectors';
import { captureMessage } from 'ContentEditorUI/lib/exceptions';
import { getIsEmbeddedEmailEditorRoute } from 'ContentEditorUI/redux/selectors/routingSelectors';
import { getIsContentFlaggedOrViolated, getIsSms, getSecurityState } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import { getIsEmail } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
const mapStateToProps = state => ({
  fatalError: getFatalError(state),
  canContactSupport: getCanContactSupport(state),
  isEmbeddedEmailEditorRoute: getIsEmbeddedEmailEditorRoute(state),
  isContentFlaggedOrViolated: getIsContentFlaggedOrViolated(state),
  securityState: getSecurityState(state),
  isEmail: getIsEmail(state),
  isSms: getIsSms(state)
});
const mapDispatchToProps = {};
class ApplicationErrorContainer extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      caughtUnknownError: false
    };
  }
  // Top-level error boundary to show generic error message (e.g. we shouldn't rely on this, it is only
  // to catch unexpected errors that bubble all the way to the top)
  static getDerivedStateFromError() {
    return {
      caughtUnknownError: true
    };
  }
  componentDidUpdate(prevProps, prevState) {
    if (this.props.hasSkeleton) {
      if (this.hasFatalError() && !this.hasFatalError(prevProps) || this.props.hasRequiredAccess === false && typeof prevProps.hasRequiredAccess /* eslint-disable-next-line valid-typeof */ === undefined || this.state.caughtUnknownError === true && prevState.caughtUnknownError === false || this.shouldRenderContentFlaggedOrViolatedPage) {
        document.getElementsByTagName('body')[0].classList.add('has-app-errors');
      }
    }
    if (this.hasFatalError()) {
      const {
        fatalError,
        shouldTrackLoadError
      } = this.props;
      const errorType = fatalError.responseJSON ? fatalError.responseJSON.errorType : null;
      const appErrorCategory = FATAL_ERROR_CODES[fatalError.status];
      if (shouldTrackLoadError) {
        if (errorType === FATAL_ERROR_TYPES.NOT_PERMISSIONED) {
          captureMessage(`Load error - ${FATAL_ERROR_TYPES.NOT_PERMISSIONED}`, {
            extra: fatalError.responseJSON
          });
        } else if (appErrorCategory === APP_ERRORS.NOT_ALLOWED) {
          captureMessage(`Load error - ${APP_ERRORS.NOT_ALLOWED}`, {
            extra: fatalError.responseJSON
          });
        }
      }
    }
  }
  componentDidCatch(error) {
    captureMessage(error);
  }
  get shouldRenderContentFlaggedOrViolatedPage() {
    const {
      isEmail,
      isSms,
      isContentFlaggedOrViolated
    } = this.props;
    return (isEmail || isSms) && isContentFlaggedOrViolated;
  }
  hasFatalError(props = this.props) {
    const {
      fatalError
    } = props;
    return !isEmpty(fatalError);
  }
  render() {
    const {
      children,
      fatalError,
      canContactSupport,
      hasRequiredAccess,
      ErrorWrapperComponent,
      isEmbeddedEmailEditorRoute,
      securityState
    } = this.props;
    const {
      caughtUnknownError
    } = this.state;
    if (this.hasFatalError()) {
      const errorType = fatalError.responseJSON ? fatalError.responseJSON.errorType : null;
      const appErrorCategory = FATAL_ERROR_CODES[fatalError.status];
      if (appErrorCategory) {
        return /*#__PURE__*/_jsx(ErrorWrapperComponent, {
          children: /*#__PURE__*/_jsx(ErrorPage, {
            appErrorCategory: appErrorCategory,
            responseErrorType: errorType,
            canContactSupport: canContactSupport,
            shouldNotRenderDashboardLink: isEmbeddedEmailEditorRoute
          })
        });
      }
      return /*#__PURE__*/_jsx(ErrorWrapperComponent, {
        children: /*#__PURE__*/_jsx(UnknownAppError, {
          canContactSupport: canContactSupport
        })
      });
    } else if (hasRequiredAccess === false) {
      // Note, hasRequiredAccess is `null` while internal/edit endpoint is pending
      return /*#__PURE__*/_jsx(ErrorWrapperComponent, {
        children: /*#__PURE__*/_jsx(UnauthorizedPage, {
          canContactSupport: canContactSupport
        })
      });
    } else if (caughtUnknownError) {
      return /*#__PURE__*/_jsx(ErrorWrapperComponent, {
        children: /*#__PURE__*/_jsx(UnknownAppError, {
          canContactSupport: canContactSupport
        })
      });
    } else if (this.shouldRenderContentFlaggedOrViolatedPage) {
      return /*#__PURE__*/_jsx(ErrorWrapperComponent, {
        children: /*#__PURE__*/_jsx(SecurityViolationPage, {
          canContactSupport: canContactSupport,
          securityState: securityState
        })
      });
    }
    return children;
  }
}
ApplicationErrorContainer.propTypes = {
  children: PropTypes.node,
  fatalError: PropTypes.object,
  hasRequiredAccess: PropTypes.bool,
  canContactSupport: PropTypes.bool.isRequired,
  isEmbeddedEmailEditorRoute: PropTypes.bool,
  isContentFlaggedOrViolated: PropTypes.bool,
  isEmail: PropTypes.bool,
  isSms: PropTypes.bool,
  securityState: PropTypes.string,
  // Need an optional error wrapper prop until email also uses new skeleton HTML
  ErrorWrapperComponent: PropTypes.elementType,
  hasSkeleton: PropTypes.bool.isRequired,
  shouldTrackLoadError: PropTypes.bool.isRequired
};
ApplicationErrorContainer.defaultProps = {
  // Default error wrapper is a no-op fragment
  ErrorWrapperComponent: Fragment,
  shouldTrackLoadError: false
};
export default connect(mapStateToProps, mapDispatchToProps)(ApplicationErrorContainer);